import dayjs from 'dayjs'
import { componentText } from '@schmancy/mixins/component-text'

/** One instant rendered through `Intl.DateTimeFormat(locale, …)` — never dayjs's
 * English/12-hour tokens — so segment order and the 12/24-hour convention follow
 * the locale passed in. `omitYear` drops the year from a range's earlier
 * endpoint when both fall in the same year. */
function instant(date: Date, type: 'date' | 'datetime-local', locale: string, omitYear: boolean): string {
	const day = new Intl.DateTimeFormat(locale, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		...(omitYear ? {} : { year: 'numeric' as const }),
	}).format(date)
	return type === 'datetime-local' ? `${day}, ${timeOf(date, locale)}` : day
}

function timeOf(date: Date, locale: string): string {
	return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)
}

/**
 * Formats a single naive local wall-clock value (`YYYY-MM-DD` or
 * `YYYY-MM-DDTHH:mm`) for display. Shares its whole formatting core with
 * `formatDateRange` below, so a lone instant and a range endpoint carrying the
 * same value render the same string under the same locale — the agreement
 * `<schmancy-date-field>` exists to hold against `<schmancy-date-range>`.
 *
 * The `locale` argument is the caller's authority, not the browser's: every
 * component in this library passes `this.locale` (theme context, falling back
 * to `navigator.language`), so a themed locale governs both controls where a
 * native `<input type="date">` would silently follow the browser instead.
 *
 * `format`, when given, is the whole of what renders — the shared default
 * (weekday, month, day, year, plus the time under `datetime-local`) is not
 * merged into it — so a caller dropping the weekday is not handed one back.
 */
export function formatDateInstant(
	value: string,
	type: 'date' | 'datetime-local',
	placeholder: string,
	locale: string,
	format?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
): string {
	if (!value) return placeholder
	// dayjs, unlike native Date, parses a date-only string as local midnight —
	// matching a datetime-local string's own local-time parse.
	const date = dayjs(value).toDate()
	if (Number.isNaN(date.getTime())) return placeholder
	return format ? new Intl.DateTimeFormat(locale, format).format(date) : instant(date, type, locale, false)
}

/**
 * Spells a `Date` as the naive local wall-clock string `value`, `min` and `max`
 * all document — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` — off the local getters, so
 * the digits are the ones a clock on the wall beside the user reads.
 *
 * The inverse of `formatDateInstant`'s parse, and the producer no consumer could
 * previously spell: `.toISOString()` is UTC-Z, which the field's dayjs
 * comparison reads as a different instant and which `<schmancy-calendar>` and a
 * native `datetime-local` input cannot read at all. A consumer whose bound comes
 * from a `Date` — `new Date()`, a Firestore timestamp — passes it through here.
 */
export function toLocalWallClock(date: Date, type: 'date' | 'datetime-local'): string {
	const pad = (n: number) => String(n).padStart(2, '0')
	const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
	return type === 'datetime-local' ? `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}` : day
}

/** Formats a `start`/`end` pair through the same `instant` core as
 * `formatDateInstant`. A `datetime-local` range always carries both endpoints'
 * times; only the year is elided from the earlier endpoint when both fall in the
 * same year. A partial range (only one endpoint set) is labelled rather than
 * hidden behind `placeholder`. */
export function formatDateRange(
	fromValue: string,
	toValue: string,
	type: 'date' | 'datetime-local',
	placeholder: string,
	locale: string,
): string {
	if (!fromValue && !toValue) return placeholder

	if (!fromValue || !toValue) {
		const only = dayjs(fromValue || toValue).toDate()
		if (Number.isNaN(only.getTime())) return placeholder
		return componentText(fromValue ? 'dateRange.startOnly' : 'dateRange.endOnly', {
			date: instant(only, type, locale, false),
		})
	}

	const fromDate = dayjs(fromValue).toDate()
	const toDate = dayjs(toValue).toDate()
	if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) return placeholder

	if (fromDate.toDateString() === toDate.toDateString()) {
		return type === 'datetime-local'
			? `${instant(fromDate, type, locale, false)} - ${timeOf(toDate, locale)}`
			: instant(fromDate, type, locale, false)
	}

	const sameYear = fromDate.getFullYear() === toDate.getFullYear()
	return `${instant(fromDate, type, locale, sameYear)} - ${instant(toDate, type, locale, false)}`
}
